import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  RenameLibraryResourceModal_UpdateGroupLibraryFolder,
  RenameLibraryResourceModal_UpdateGroupLibraryFolderVariables,
} from './graphql-types/RenameLibraryResourceModal_UpdateGroupLibraryFolder';
import {
  RenameLibraryResourceModal_UpdateVersionFileName,
  RenameLibraryResourceModal_UpdateVersionFileNameVariables,
} from './graphql-types/RenameLibraryResourceModal_UpdateVersionFileName';

const UPDATE_FOLDER_NAME = gql`
  mutation RenameLibraryResourceModal_UpdateGroupLibraryFolder(
    $input: UpdateGroupLibraryFolderNameInput!
  ) {
    updateGroupLibraryFolderName(input: $input)
  }
`;

const UPDATE_FILE_NAME = gql`
  mutation RenameLibraryResourceModal_UpdateVersionFileName(
    $input: UpdateVersionFileNameInput!
  ) {
    updateVersionFileName(input: $input) {
      versionFile {
        _id
        file {
          _id
          name
        }
      }
    }
  }
`;

export const RenameLibraryResourceModal: React.FC<{
  onClose: () => void;
  isOpen: boolean;
  initialName: string;
  versionFileId?: string;
  libraryFolderId?: string;
}> = ({ onClose, isOpen, versionFileId, libraryFolderId, initialName }) => {
  const toast = useToast();
  const resourceName = versionFileId
    ? initialName.split('.').slice(0, -1).join('')
    : initialName;
  const extension = versionFileId
    ? initialName.split('.').slice(-1).join('')
    : '';
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    defaultValues: {
      elementName: resourceName ?? '',
    },
  });

  const [renameFile, { loading: isFileRenaming }] = useMutation<
    RenameLibraryResourceModal_UpdateVersionFileName,
    RenameLibraryResourceModal_UpdateVersionFileNameVariables
  >(UPDATE_FILE_NAME, {
    refetchQueries: ['GroupResourcesPane_LibraryFolderContents'],
    awaitRefetchQueries: true,
  });

  const [renameFolder, { loading: isFolderRenaming }] = useMutation<
    RenameLibraryResourceModal_UpdateGroupLibraryFolder,
    RenameLibraryResourceModal_UpdateGroupLibraryFolderVariables
  >(UPDATE_FOLDER_NAME, {
    refetchQueries: ['GroupResourcesPane_LibraryFolderContents'],
    awaitRefetchQueries: true,
  });

  const onSubmitFile = handleSubmit(async (data) => {
    try {
      await renameFile({
        variables: {
          input: {
            versionFileId: versionFileId ?? '',
            name: data.elementName + '.' + extension,
          },
        },
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Error while renaming the file, please try again',
        status: 'error',
      });
    }
  });

  const onSubmitFolder = handleSubmit(async (data) => {
    try {
      await renameFolder({
        variables: {
          input: {
            libraryFolderId: libraryFolderId ?? '',
            name: data.elementName,
          },
        },
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Error while renaming the folder, please try again',
        status: 'error',
      });
    }
  });

  const isFolder = !!libraryFolderId;

  if (!(versionFileId || libraryFolderId)) {
    return null;
  }

  const isLoading = isFileRenaming || isFolderRenaming;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Rename {isFolder ? 'Folder' : 'File'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isRequired isInvalid={!!errors.elementName}>
            <FormLabel>{isFolder ? 'Folder' : 'File'} Name</FormLabel>

            <Controller
              control={control}
              name="elementName"
              render={({ field: { value, onChange, ref } }) => (
                <InputGroup>
                  <Input
                    value={value}
                    onChange={onChange}
                    ref={ref}
                    autoFocus
                  />
                  {extension && <InputRightAddon>.{extension}</InputRightAddon>}
                </InputGroup>
              )}
              rules={{
                validate: {
                  required: (value) =>
                    value.trim().length > 0 ||
                    `The ${isFolder ? 'folder' : 'file'} name cannot be empty`,
                },
              }}
            />
            <FormErrorMessage>{errors.elementName?.message}</FormErrorMessage>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <Button
              onClick={onClose}
              variant="outline"
              colorScheme="gray"
              isDisabled={isLoading}
            >
              Close
            </Button>

            <Button
              type="submit"
              isLoading={isLoading}
              onClick={isFolder ? onSubmitFolder : onSubmitFile}
            >
              Rename
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
